import { useCallback, useEffect, useRef } from "react";
import { Dialog as RadixDialog } from "radix-ui";
import { clsx } from "clsx";
import { X } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

import { Button } from "../Button/Button";

export type DialogProps = Pick<
  RadixDialog.DialogProps,
  "open" | "onOpenChange"
> & {
  title?: string;
  titleClassName?: string;
  /**
   * Accessible name used when there is no visible `title` (headerless dialogs).
   * Every dialog needs an accessible name, so provide this whenever `title` is
   * omitted.
   */
  ariaLabel?: string;
  /**
   * Full-bleed content rendered above the padded body, edge-to-edge with the
   * dialog's rounded top corners (e.g. a hero image). When present without a
   * `title`, the close button floats over the top-right of this area.
   */
  media?: React.ReactNode;
  footer?: React.ReactNode;
  rootProps?: Omit<Partial<RadixDialog.DialogProps>, "open" | "onOpenChange">;
  contentProps?: Partial<RadixDialog.DialogContentProps>;
  closeProps?: Partial<RadixDialog.DialogCloseProps>;
} & (
    | {
        children: React.ReactNode;
        description?: string | JSX.Element;
      }
    | {
        children?: React.ReactNode;
        description: string | JSX.Element;
      }
  );

export function Dialog({
  title,
  titleClassName,
  ariaLabel,
  media,
  description,
  footer,
  open,
  onOpenChange,
  children,
  rootProps,
  contentProps,
  closeProps,
}: DialogProps) {
  const hasHeader = Boolean(title);
  const openerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    onOpenAutoFocus: contentOnOpenAutoFocus,
    onCloseAutoFocus: contentOnCloseAutoFocus,
    ...restContentProps
  } = contentProps ?? {};

  // A dialog opened from a menu item outlives that item: the menu unmounts it
  // and focuses its own trigger, so follow focus that lands on the page while
  // the dialog is open. Focus in a stacked dialog or a portaled menu, select
  // or popover belongs to this dialog's session and is gone by close.
  const trackOpener = useCallback((event: FocusEvent) => {
    if (
      event.target instanceof HTMLElement &&
      !contentRef.current?.contains(event.target) &&
      !event.target.closest(
        '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]',
      )
    ) {
      openerRef.current = event.target;
    }
  }, []);

  useEffect(
    () => () => document.removeEventListener("focusin", trackOpener),
    [trackOpener],
  );

  // Radix only returns focus to a Dialog.Trigger, which this component never
  // renders, so remember whatever had focus when the dialog opened.
  const handleOpenAutoFocus = (event: Event) => {
    openerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    document.addEventListener("focusin", trackOpener);
    if (contentOnOpenAutoFocus) {
      contentOnOpenAutoFocus(event);
    } else {
      // Keep focus on the opener so the close button doesn't look focused.
      event.preventDefault();
    }
  };

  const handleCloseAutoFocus = (event: Event) => {
    document.removeEventListener("focusin", trackOpener);
    const opener = openerRef.current;
    openerRef.current = null;
    contentOnCloseAutoFocus?.(event);
    if (event.defaultPrevented) {
      return;
    }
    if (opener?.isConnected) {
      event.preventDefault();
      opener.focus();
    }
  };

  const closeButton = onOpenChange && (
    <RadixDialog.Close asChild {...closeProps}>
      <Button aria-label="Close dialog" variant="icon-ghost" size="md">
        <X aria-hidden />
      </Button>
    </RadixDialog.Close>
  );

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} {...rootProps}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed inset-0 flex items-center justify-center bg-black/50 p-12">
          <RadixDialog.Content
            {...restContentProps}
            ref={contentRef}
            onOpenAutoFocus={handleOpenAutoFocus}
            onCloseAutoFocus={handleCloseAutoFocus}
            className={twMerge(
              clsx(
                "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut bg-surface-default shadow-elevation-200 flex max-h-full w-125 flex-col overflow-hidden rounded-lg",
                restContentProps.className,
              ),
            )}
          >
            {/* Every Radix dialog needs a Title for its accessible name. When
                there's no visible header, render it visually hidden. */}
            {!hasHeader && (
              <RadixDialog.Title className="sr-only">
                {ariaLabel ?? ""}
              </RadixDialog.Title>
            )}

            {media && (
              <div className="relative flex-none">
                {media}
                {!hasHeader && closeButton && (
                  <div className="absolute top-3 right-3 z-10">
                    {closeButton}
                  </div>
                )}
              </div>
            )}

            {hasHeader && (
              <div className="flex flex-none items-center justify-between pt-6 pr-6 pb-3 pl-6">
                <RadixDialog.Title
                  className={twMerge("text-heading-lg", titleClassName)}
                >
                  {title}
                </RadixDialog.Title>
                {closeButton}
              </div>
            )}

            {!hasHeader && !media && closeButton && (
              <div className="absolute top-3 right-3 z-10">{closeButton}</div>
            )}

            <div
              className={twMerge(
                "text-body-sm min-h-0 flex-1 overflow-auto px-6 pb-6",
                !hasHeader && "pt-6",
              )}
            >
              {description && (
                <RadixDialog.Description asChild>
                  <div className="text-textSubtle pb-3">{description}</div>
                </RadixDialog.Description>
              )}
              {!description && <RadixDialog.Description className="sr-only" />}
              {children}
            </div>
            {footer && (
              <div className="border-borderDefault flex flex-none items-center justify-end gap-2 border-t px-6 pt-4 pb-5">
                {footer}
              </div>
            )}
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
interface DialogActionsWrapperProps {
  children: React.ReactNode;
  className?: string;
}
export function DialogActionsWrapper({
  children,
  className,
}: DialogActionsWrapperProps) {
  return (
    <div
      className={twMerge("flex items-center justify-end gap-2 pt-3", className)}
    >
      {children}
    </div>
  );
}
